import {
  gameConfig,
  REAL_MS_PER_GAME_MINUTE,
} from '../config/gameConfig';
import type { GameState } from '../models/GameState';
import type { GameTime } from '../models/GameTime';
import type { ParkingLot } from '../models/ParkingLot';
import { HISTORY_DAYS } from '../models/Finance';
import { RandomGenerator } from '../utils/random';
import { roundMoney } from '../utils/math';
import { economyConfig } from '../config/economyConfig';
import { clamp } from '../utils/math';
import { CompetitorEngine } from './CompetitorEngine';
import { EconomyEngine } from './EconomyEngine';
import { EventEngine } from './EventEngine';
import { GameClock } from './GameClock';
import { MarketEngine } from './MarketEngine';
import { ReputationEngine } from './ReputationEngine';
import { UpgradeEngine } from './UpgradeEngine';
import { ValuationEngine } from './ValuationEngine';
import { VehicleEngine } from './VehicleEngine';

/** Events emitted by a simulation update, for the store/UI to react to. */
export interface SimTickResult {
  /** Number of fixed simulation ticks processed this update. */
  ticks: number;
  /** Whether a new game-day began during this update. */
  dayChanged: boolean;
  /** Whether a new game-month began during this update. */
  monthChanged: boolean;
}

/**
 * The simulation stepper (spec sections 1 & 4).
 *
 * Responsibilities:
 *  - Convert real elapsed time into fixed-size in-game ticks based on the
 *    current game speed. Ticks are decoupled from render FPS (spec section 4).
 *  - Advance each subsystem by exactly one fixed tick.
 *
 * It mutates the provided GameState in place and stays completely free of
 * Phaser so it can run in tests, on mobile, or on a server. In Phase 1 the
 * only subsystem is the clock; later phases add demand/vehicles/economy here.
 */
export class GameSimulation {
  /** Leftover real-time (ms) not yet consumed by a whole tick. */
  private accumulatorMs = 0;

  /** Safety cap so a huge dt (e.g. tab regained focus) can't freeze the loop. */
  private readonly maxTicksPerUpdate = 100_000;

  reset(): void {
    this.accumulatorMs = 0;
  }

  /**
   * Advance the simulation using the real time elapsed since the last update.
   * Returns a summary of what happened for the caller to broadcast.
   */
  update(state: GameState, realDeltaMs: number): SimTickResult {
    const result: SimTickResult = {
      ticks: 0,
      dayChanged: false,
      monthChanged: false,
    };

    if (state.paused || state.speed === 0) {
      // Do not accumulate while paused; resume cleanly afterwards.
      this.accumulatorMs = 0;
      return result;
    }

    // At speed N, in-game time flows N times faster.
    const msPerTick =
      (REAL_MS_PER_GAME_MINUTE * gameConfig.simulationTickMinutes) /
      state.speed;

    const totalMs = this.accumulatorMs + realDeltaMs;

    // Compute whole ticks by division rather than a subtraction loop, so we do
    // not accumulate floating-point drift over long deltas (which would make
    // the clock lose ticks and break determinism). A tiny epsilon absorbs
    // representation error at exact tick boundaries.
    const epsilon = msPerTick * 1e-9;
    let ticks = Math.floor((totalMs + epsilon) / msPerTick);

    if (ticks > this.maxTicksPerUpdate) ticks = this.maxTicksPerUpdate;

    // Keep the unconsumed remainder for the next update.
    this.accumulatorMs = totalMs - ticks * msPerTick;

    // Deterministic RNG loaded from / saved back to state, so randomness
    // survives save/load and is reproducible (important for multiplayer).
    const rng = new RandomGenerator(state.rngState);
    for (let i = 0; i < ticks; i++) {
      this.step(state, result, rng);
    }
    state.rngState = rng.getState();

    result.ticks = ticks;
    return result;
  }

  /** Advance exactly one fixed tick (one game-minute by default). */
  private step(
    state: GameState,
    result: SimTickResult,
    rng: RandomGenerator
  ): void {
    const before = state.time;
    const after = GameClock.advance(before, gameConfig.simulationTickMinutes);
    state.time = after;

    const dayChanged = GameClock.crossedDay(before, after);
    const monthChanged = GameClock.crossedMonth(before, after);
    if (dayChanged) result.dayChanged = true;
    if (monthChanged) result.monthChanged = true;

    // Retire finished events; roll for new ones once per day (spec 22/36).
    EventEngine.expire(state, after.totalMinutes);
    if (dayChanged) EventEngine.rollDaily(state, after, rng);

    // Advance each parking lot's vehicle simulation by one fixed tick and
    // collect parking revenue (credited immediately to the company's cash).
    let revenue = 0;
    for (const lot of state.parkingLots) {
      // How much of the district's demand this lot wins vs competitors,
      // scaled by any active events (concerts lift, outages/rain suppress).
      const competitionMult = MarketEngine.competitionMultiplier(
        lot,
        state.competitors
      );
      const eventMult = EventEngine.demandMultiplier(
        state,
        lot.districtId,
        after.totalMinutes
      );
      revenue += VehicleEngine.update(
        lot,
        gameConfig.simulationTickMinutes,
        after,
        rng,
        competitionMult * eventMult
      );
    }
    if (revenue > 0) {
      state.player.cash = roundMoney(state.player.cash + revenue);
      state.player.totalRevenue = roundMoney(
        state.player.totalRevenue + revenue
      );
      state.finance.todayRevenue = roundMoney(
        state.finance.todayRevenue + revenue
      );
    }

    // Settle the day's profit & loss at each midnight crossing (spec 13/30).
    if (dayChanged) {
      this.settleDailyPnL(state, before);
      // Competitors run their (abstract) economy under the same rules.
      CompetitorEngine.settleDaily(state, after);
    }
    // Competitors make strategic decisions once a month (spec section 20).
    if (monthChanged) CompetitorEngine.decideMonthly(state, rng);
  }

  /**
   * Charge the day's operating expenses, record the completed day's P&L, and
   * reset the daily accumulators. `endedDay` is the time of the day that just
   * finished (used for the record's date).
   */
  private settleDailyPnL(state: GameState, endedDay: GameTime): void {
    const expense = EconomyEngine.computeTotalDailyExpense(state.parkingLots);

    // Apply per-lot expense to lifetime lot P&L, and drift reputation toward
    // the day's customer satisfaction (spec section 16).
    for (const lot of state.parkingLots) {
      const lotExpense = EconomyEngine.computeDailyExpenses(lot).total;
      lot.expenses = roundMoney(lot.expenses + lotExpense);
      lot.profit = roundMoney(lot.revenue - lot.expenses);
      ReputationEngine.updateDaily(lot);
      this.applyDynamicPricing(lot);
    }

    // Company reputation = average of lot reputations (spec section 5).
    if (state.parkingLots.length > 0) {
      const sumRep = state.parkingLots.reduce((s, l) => s + l.reputation, 0);
      state.player.reputation = sumRep / state.parkingLots.length;
    }

    if (expense > 0) {
      state.player.cash = roundMoney(state.player.cash - expense);
      state.player.totalExpense = roundMoney(
        state.player.totalExpense + expense
      );
    }

    const revenue = state.finance.todayRevenue;
    const profit = roundMoney(revenue - expense);
    state.player.totalProfit = roundMoney(state.player.totalProfit + profit);

    const servedTotal = state.parkingLots.reduce(
      (sum, l) => sum + l.vehiclesServed,
      0
    );
    const rejectedTotal = state.parkingLots.reduce(
      (sum, l) => sum + l.vehiclesRejected,
      0
    );

    state.finance.history.push({
      year: endedDay.year,
      month: endedDay.month,
      day: endedDay.day,
      revenue,
      expense,
      profit,
      cash: state.player.cash,
      vehiclesServed: servedTotal - state.finance.lastServedTotal,
      vehiclesRejected: rejectedTotal - state.finance.lastRejectedTotal,
    });
    if (state.finance.history.length > HISTORY_DAYS) {
      state.finance.history.shift();
    }

    state.finance.lastServedTotal = servedTotal;
    state.finance.lastRejectedTotal = rejectedTotal;
    state.finance.todayRevenue = 0;

    // Company valuation + campaign objective (spec section 38).
    const valuation = ValuationEngine.compute(state);
    state.player.companyValue = valuation.total;
    state.player.marketShare = valuation.marketSharePct;
    if (!state.objectiveAchieved && ValuationEngine.isObjectiveMet(valuation)) {
      state.objectiveAchieved = true;
      state.objectiveAchievedAtMinute = endedDay.totalMinutes;
      state.notifications.push({
        id: `objective-${endedDay.totalMinutes}`,
        message: '🏆 Goal reached — you built a parking empire!',
        kind: 'positive',
        atMinute: endedDay.totalMinutes,
      });
    }
  }

  /**
   * Dynamic Pricing upgrade (spec sections 17/18): nudge the lot price toward
   * demand — raise it when the lot is turning cars away, lower it when demand
   * is soft — one small step per day, within configured bounds.
   */
  private applyDynamicPricing(lot: ParkingLot): void {
    if (!UpgradeEngine.aggregate(lot).dynamicPricing) return;
    const step = economyConfig.PRICE_STEP;
    let price = lot.parkingPrice;
    if (lot.rejectionRateEma > 0.25) {
      price += step; // too busy -> charge more
    } else if (lot.rejectionRateEma < 0.05) {
      price -= step; // plenty of room -> attract more
    }
    lot.parkingPrice = clamp(price, economyConfig.PRICE_MIN, economyConfig.PRICE_MAX);
  }
}
